import { useTranslation } from 'react-i18next';
import { Check, PanelLeft, PanelTop } from 'lucide-react';
import { PrefSection, type PrefVariant } from '@/components/PrefSection';
import { useTagPlacement, type TagPlacement } from '@/hooks/useTagPlacement';
import { cn } from '@/lib/utils';

/**
 * Profile-page control for where a patient's diet restrictions + motivations
 * appear — in the rail (sidebar) or as a strip across the top header. Applies
 * immediately, persists to this browser.
 */
const OPTIONS: Array<{ value: TagPlacement; label: string; icon: typeof PanelLeft }> = [
  { value: 'sidebar', label: 'Sidebar', icon: PanelLeft },
  { value: 'header', label: 'Top header', icon: PanelTop },
];

export const TagPlacementSwitcher = ({ variant = 'card' }: { variant?: PrefVariant }) => {
  const { t } = useTranslation();
  const [placement, setPlacement] = useTagPlacement();

  return (
    <PrefSection
      variant={variant}
      icon={PanelTop}
      title={t('Diet & motivations placement')}
      description={t(
        'Show a patient’s dietary restrictions and motivations in the sidebar or across the top header. Saved to this browser.',
      )}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        {OPTIONS.map((o) => {
          const active = placement === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => setPlacement(o.value)}
              aria-pressed={active}
              className={cn(
                'flex items-center gap-2 rounded-md border p-3 transition-colors',
                active ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:bg-muted',
              )}
            >
              <o.icon className="h-4 w-4 shrink-0 text-primary" />
              <span className="flex flex-1 items-center gap-1.5 text-sm font-medium">
                {t(o.label)}
                {active && <Check className="ml-auto h-3.5 w-3.5 text-primary" />}
              </span>
            </button>
          );
        })}
      </div>
    </PrefSection>
  );
};
